import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { GuestDataProps } from "../GuestForm";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Check, CheckCircle, Edit, Info } from "lucide-react";
import { PMSUserVerificationDialog } from "../PMSUserVerificationDialog";
import { Badge } from "../ui/badge";
import {
  PrivacyPolicyDialog,
  useStructurePrivacy,
} from "@/components/PrivacyPolicyDialog";

export default function Verification({
  data,
  loading,
  onChange,
  onVerifyUser,
}: {
  data: GuestDataProps;
  loading: boolean;
  onChange: (data: GuestDataProps) => void;
  onVerifyUser?: (
    userId: string | null,
    fromPms: boolean,
    goToDocumentStep?: boolean,
  ) => void;
}) {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPrivacyInfoDialogOpen, setIsPrivacyInfoDialogOpen] = useState(false);
  const [policyModalType, setPolicyModalType] = useState<
    "privacy" | "marketing"
  >("privacy");
  const [isCompleted, setIsCompleted] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const { privacyPolicyHtml, marketingPolicyHtml } = useStructurePrivacy();

  const onOpenInfoPrivacyPolicy = (
    type: "privacy" | "marketing" = "privacy",
  ) => {
    setPolicyModalType(type);
    setIsPrivacyInfoDialogOpen(true);
  };

  const handleChange = (field: keyof GuestDataProps, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const getErrorMessage = (errorCode: string): string => {
    return t(
      `verification.errors.${errorCode}`,
      t("verification.errors.required_field"),
    );
  };

  const hasError = (errorCode: string): boolean => {
    return errors.includes(errorCode);
  };

  // Apri la dialog automaticamente quando si accede alla tab Verification
  useEffect(() => {
    setIsDialogOpen(true);
  }, []);

  // Validazione dei campi
  useEffect(() => {
    const errors: string[] = [];

    if (!data.privacyPolicy) {
      errors.push("required_privacy_policy");
    }

    if (data.invoiceRequest) {
      if (!data.companyName || data.companyName.trim() === "") {
        errors.push("required_company_name");
      }
      if (!data.vatNumber || data.vatNumber.trim() === "") {
        errors.push("required_vat_number");
      }
      if (!data.invoiceAddress || data.invoiceAddress.trim() === "") {
        errors.push("required_invoice_address");
      }
      if (!data.invoiceCap || data.invoiceCap.trim() === "") {
        errors.push("required_invoice_cap");
      }
      if (!data.invoiceCity || data.invoiceCity.trim() === "") {
        errors.push("required_invoice_city");
      }
      if (
        (!data.recipientCode || data.recipientCode.trim() === "") &&
        (!data.pecEmail || data.pecEmail.trim() === "")
      ) {
        errors.push("required_recipient_or_pec");
      }
    }

    setErrors(errors);
  }, [data]);

  const handleUserSelected = (
    userId: string | null,
    fromPms: boolean,
    goToDocumentStep?: boolean,
  ) => {
    onVerifyUser?.(userId, fromPms, goToDocumentStep);
  };

  const handleModifyData = () => {
    // Permetti la modifica dei dati quando richiesto
    onVerifyUser?.(null, false, true); // true indica di tornare allo step 3
    setIsCompleted(false);
  };

  // Prepara i dati di ricerca basati sui dati anagrafici compilati
  const getSearchData = () => {
    const searchParams: any = {};

    if (data.name) {
      searchParams.name = data.name;
    }
    if (data.email) {
      searchParams.email = data.email;
    }
    if (data.birthDate) {
      // Formatta la data nel formato YYYY-MM-DD per l'API
      const year = data.birthDate.getFullYear();
      const month = String(data.birthDate.getMonth() + 1).padStart(2, "0");
      const day = String(data.birthDate.getDate()).padStart(2, "0");
      searchParams.dateOfBirth = `${year}-${month}-${day}`;
    }

    return Object.keys(searchParams).length > 0 ? searchParams : undefined;
  };

  const isFormValid = () => {
    // Verifica che la privacy policy sia accettata
    if (!data.privacyPolicy) return false;

    // Se è richiesta la fattura, verifica che i campi obbligatori siano compilati
    if (data.invoiceRequest) {
      if (!data.companyName?.trim()) return false;
      if (!data.vatNumber?.trim()) return false;
      if (!data.invoiceAddress?.trim()) return false;
      if (!data.cap?.trim()) return false;
      if (!data.invoiceCity?.trim()) return false;
    }

    return true;
  };

  return (
    <div className="space-y-6">
      {/* Nascondi i campi quando è completato */}
      {!isCompleted && (
        <>
          {/* Privacy e Marketing */}
          <div className="col-span-2 space-y-2">
            <div>
              <Label className="hover:bg-accent/50 flex items-start gap-3 rounded-lg border p-3 has-[[aria-checked=true]]:border-blue-600 has-[[aria-checked=true]]:bg-blue-50 dark:has-[[aria-checked=true]]:border-blue-900 dark:has-[[aria-checked=true]]:bg-blue-950">
                <Checkbox
                  disabled={loading}
                  id="privacy-policy"
                  checked={data.privacyPolicy}
                  onCheckedChange={(v) => handleChange("privacyPolicy", !!v)}
                  className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white dark:data-[state=checked]:border-blue-700 dark:data-[state=checked]:bg-blue-700"
                />
                <div className="grid gap-1.5 font-normal">
                  <p className="text-sm leading-none font-medium">
                    {t("verification.privacyPolicy")}
                    <span className="ml-1 text-destructive">*</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size={"sm"}
                      className="ml-2 inline-flex align-middle cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onOpenInfoPrivacyPolicy("privacy");
                      }}
                      aria-label={t("verification.privacyPolicy")}
                    >
                      <Info className="h-4 w-4" />
                    </Button>
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {t("verification.privacyPolicyDescription")}
                  </p>
                </div>
              </Label>
              {hasError("required_privacy_policy") && (
                <p className="text-sm text-red-500 mt-1">
                  {getErrorMessage("required_privacy_policy")}
                </p>
              )}
            </div>

            <Label className="hover:bg-accent/50 flex items-start gap-3 rounded-lg border p-3 has-[[aria-checked=true]]:border-blue-600 has-[[aria-checked=true]]:bg-blue-50 dark:has-[[aria-checked=true]]:border-blue-900 dark:has-[[aria-checked=true]]:bg-blue-950">
              <Checkbox
                disabled={loading}
                id="marketing-policy"
                checked={data.marketingPolicy}
                onCheckedChange={(v) => handleChange("marketingPolicy", !!v)}
                className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white dark:data-[state=checked]:border-blue-700 dark:data-[state=checked]:bg-blue-700"
              />
              <div className="grid gap-1.5 font-normal">
                <p className="text-sm leading-none font-medium">
                  {t("verification.marketingConsent")}
                  <Button
                    type="button"
                    variant="ghost"
                    size={"sm"}
                    className="ml-2 inline-flex align-middle cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onOpenInfoPrivacyPolicy("marketing");
                    }}
                    aria-label={t("verification.marketingConsent")}
                  >
                    <Info className="h-4 w-4" />
                  </Button>
                </p>
                <p className="text-muted-foreground text-sm">
                  {t("verification.marketingConsentDescription")}
                </p>
              </div>
            </Label>

            <Label className="hover:bg-accent/50 flex items-start gap-3 rounded-lg border p-3 has-[[aria-checked=true]]:border-blue-600 has-[[aria-checked=true]]:bg-blue-50 dark:has-[[aria-checked=true]]:border-blue-900 dark:has-[[aria-checked=true]]:bg-blue-950">
              <Checkbox
                disabled={loading}
                id="invoice-request"
                checked={data.invoiceRequest}
                onCheckedChange={(v) => handleChange("invoiceRequest", !!v)}
                className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white dark:data-[state=checked]:border-blue-700 dark:data-[state=checked]:bg-blue-700"
              />
              <div className="grid gap-1.5 font-normal">
                <p className="text-sm leading-none font-medium">
                  {t("verification.requestInvoice")}
                </p>
                <p className="text-muted-foreground text-sm">
                  {t("verification.requestInvoiceDescription")}
                </p>
              </div>
            </Label>
          </div>

          {/* Campi Fattura (Condizionali) */}
          {data.invoiceRequest && (
            <div className="col-span-2 space-y-4 p-4 border rounded-lg">
              <h3 className="text-lg font-semibold mb-4">
                {t("verification.billingInformation")}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("verification.companyName")}</Label>
                  <Input
                    disabled={loading}
                    value={data.companyName || ""}
                    onChange={(e) =>
                      handleChange("companyName", e.target.value)
                    }
                    placeholder={t("verification.placeholders.companyName")}
                    className={
                      hasError("required_company_name") ? "border-red-500" : ""
                    }
                  />
                  {hasError("required_company_name") && (
                    <p className="text-sm text-red-500">
                      {getErrorMessage("required_company_name")}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{t("verification.vatNumber")}</Label>
                  <Input
                    disabled={loading}
                    value={data.vatNumber || ""}
                    onChange={(e) => handleChange("vatNumber", e.target.value)}
                    placeholder={t("verification.placeholders.vatNumber")}
                    className={
                      hasError("required_vat_number") ? "border-red-500" : ""
                    }
                  />
                  {hasError("required_vat_number") && (
                    <p className="text-sm text-red-500">
                      {getErrorMessage("required_vat_number")}
                    </p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>{t("verification.address")}</Label>
                  <Input
                    disabled={loading}
                    value={data.invoiceAddress || ""}
                    onChange={(e) =>
                      handleChange("invoiceAddress", e.target.value)
                    }
                    placeholder={t("verification.placeholders.address")}
                    className={
                      hasError("required_invoice_address")
                        ? "border-red-500"
                        : ""
                    }
                  />
                  {hasError("required_invoice_address") && (
                    <p className="text-sm text-red-500">
                      {getErrorMessage("required_invoice_address")}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{t("verification.postalCode")}</Label>
                  <Input
                    disabled={loading}
                    value={data.invoiceCap || ""}
                    onChange={(e) => handleChange("invoiceCap", e.target.value)}
                    placeholder={t("verification.placeholders.postalCode")}
                    className={
                      hasError("required_invoice_cap") ? "border-red-500" : ""
                    }
                  />
                  {hasError("required_invoice_cap") && (
                    <p className="text-sm text-red-500">
                      {getErrorMessage("required_invoice_cap")}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{t("verification.city")}</Label>
                  <Input
                    disabled={loading}
                    value={data.invoiceCity || ""}
                    onChange={(e) =>
                      handleChange("invoiceCity", e.target.value)
                    }
                    placeholder={t("verification.placeholders.city")}
                    className={
                      hasError("required_invoice_city") ? "border-red-500" : ""
                    }
                  />
                  {hasError("required_invoice_city") && (
                    <p className="text-sm text-red-500">
                      {getErrorMessage("required_invoice_city")}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{t("verification.recipientCode")}</Label>
                  <Input
                    disabled={loading}
                    value={data.recipientCode || ""}
                    onChange={(e) =>
                      handleChange("recipientCode", e.target.value)
                    }
                    placeholder={t("verification.placeholders.recipientCode")}
                    className={
                      hasError("required_recipient_or_pec")
                        ? "border-red-500"
                        : ""
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("verification.pecEmail")}</Label>
                  <Input
                    disabled={loading}
                    type="email"
                    value={data.pecEmail || ""}
                    onChange={(e) => handleChange("pecEmail", e.target.value)}
                    placeholder={t("verification.placeholders.pecEmail")}
                    className={
                      hasError("required_recipient_or_pec")
                        ? "border-red-500"
                        : ""
                    }
                  />
                </div>
                {hasError("required_recipient_or_pec") && (
                  <div className="col-span-2">
                    <p className="text-sm text-red-500">
                      {getErrorMessage("required_recipient_or_pec")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Dialog per verifica utente PMS */}
      <PMSUserVerificationDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onUserSelected={handleUserSelected}
        searchData={getSearchData()}
      />

      <PrivacyPolicyDialog
        open={isPrivacyInfoDialogOpen}
        onOpenChange={setIsPrivacyInfoDialogOpen}
        title={
          policyModalType === "marketing"
            ? t("verification.marketingConsent")
            : t("verification.privacyPolicy")
        }
        htmlContent={
          policyModalType === "marketing"
            ? marketingPolicyHtml
            : privacyPolicyHtml
        }
        fallbackText={t("verification.privacyPolicyDescription")}
      />

      {/* Pulsanti azione - sempre visibili */}
      <div
        className={`col-span-2 mt-6 flex gap-2 ${isCompleted ? "justify-center" : "justify-end"}`}
      >
        {isCompleted ? (
          <>
            <div className="flex flex-col items-center">
              <Badge className="bg-green-500">
                <CheckCircle />
                {t("verification.completed")}
              </Badge>
              <Button
                onClick={handleModifyData}
                variant="ghost"
                size={"sm"}
                className="flex items-center gap-2 text-primary hover:text-blue-600 text-xs mt-1"
              >
                <Edit className="h-4 w-4" />
                {t("verification.editData")}
              </Button>
            </div>
          </>
        ) : (
          <Button
            onClick={() => {
              handleUserSelected(null, false);
              setIsCompleted(true);
            }}
            disabled={!isFormValid() || loading}
            className="flex items-center gap-2"
          >
            {t("verification.complete")}
            <CheckCircle className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
